import React, {
  useEffect, useState, useCallback, useRef, useMemo,
} from 'react';
import API from '../services/api';
import { useParams, useNavigate } from 'react-router-dom';
import './TakeTest.css';
import TestWaitingPage from './TestWaitingPage';
import { usePopup } from '../context/PopupContext';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const TAB_WARN_AT = 1; // show warning from 1st tab switch onward
const TAB_LIMIT   = 3; // auto-submit on 3rd tab switch
const FS_WARN_AT  = 1; // show warning from 1st fullscreen exit onward
const FS_LIMIT    = 2; // auto-submit on 2nd fullscreen exit

// ─────────────────────────────────────────────────────────────────────────────
// MEMOIZED NAVIGATOR  (never re-renders on timer tick)
// ─────────────────────────────────────────────────────────────────────────────
const QuestionNavigator = React.memo(({
  questions, current, getNavStatus, setCurrent, setShowExplanation,
}) => (
  <div className="tt-nav-grid">
    {questions.map((_, idx) => (
      <button
        key={idx}
        className={`tt-nav-btn ${getNavStatus(idx)} ${idx === current ? 'current' : ''}`}
        onClick={() => { setCurrent(idx); setShowExplanation(false); }}
      >
        {idx + 1}
      </button>
    ))}
  </div>
));

// ─────────────────────────────────────────────────────────────────────────────
// ANTI-CHEAT OVERLAY
// ─────────────────────────────────────────────────────────────────────────────
const Overlay = ({ kind, count, limit, testName, onDismiss, autoSubmitting }) => {
  const isTab    = kind === 'tab';
  const isFinal  = autoSubmitting;
  const remaining = limit - count;

  return (
    <div className="ac-overlay-bg">
      <div className={`ac-overlay-card ${isFinal ? 'ac-final' : 'ac-warn'}`}>
        <div className="ac-overlay-icon">{isFinal ? '🚫' : '⚠️'}</div>

        <h2 className="ac-overlay-title">
          {isFinal
            ? 'Test Auto-Submitted'
            : isTab
              ? `Tab Switch Detected — Warning ${count} of ${limit - 1}`
              : `Fullscreen Exit Detected — Warning ${count} of ${limit - 1}`}
        </h2>

        <p className="ac-overlay-body">
          {isFinal
            ? `"${testName}" was automatically submitted because you exceeded the allowed number of ${isTab ? 'tab switches' : 'fullscreen exits'}. All answers saved so far have been recorded.`
            : isTab
              ? remaining > 1
                ? `You left the test window. You have ${remaining - 1} warning${remaining - 1 > 1 ? 's' : ''} left before your test is auto-submitted.`
                : `⚠️ LAST WARNING — the next tab switch will instantly auto-submit your test.`
              : remaining > 1
                ? `You exited fullscreen. You have ${remaining - 1} warning${remaining - 1 > 1 ? 's' : ''} left.`
                : `⚠️ LAST WARNING — the next fullscreen exit will instantly auto-submit your test.`}
        </p>

        {!isFinal && (
          <>
            {!isTab && (
              <p className="ac-overlay-hint">
                Press <kbd>F11</kbd> or click the button below to re-enter fullscreen.
              </p>
            )}
            <button className="ac-overlay-btn" onClick={onDismiss}>
              {isTab ? '✓ I Understand — Return to Test' : '⛶ Re-enter Fullscreen & Continue'}
            </button>
          </>
        )}

        {isFinal && (
          <p className="ac-overlay-hint" style={{ marginTop: 12 }}>
            Redirecting to your results…
          </p>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const TakeTest = ({ isMockTest = false }) => {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { showConfirm, showAlert } = usePopup();

  // Determine mock mode once synchronously — stable for the lifetime of this mount
  const isMock = useMemo(
    () => isMockTest || window.location.pathname.startsWith('/student-mock'),
    [], // eslint-disable-line
  );

  // ── Test data ──────────────────────────────────────────────────────────────
  const [test, setTest]                           = useState(null);
  const [questions, setQuestions]                 = useState([]);
  const [loading, setLoading]                     = useState(true);
  const [testStatus, setTestStatus]               = useState('loading');
  const [scheduledAt, setScheduledAt]             = useState(null);
  const [globalScheduledAt, setGlobalScheduledAt] = useState(null);

  // ── Exam state ─────────────────────────────────────────────────────────────
  const [current, setCurrent]           = useState(0);
  const [answers, setAnswers]           = useState({});
  const [marked, setMarked]             = useState(new Set());
  const [timeLeft, setTimeLeft]         = useState(0);
  const [submitted, setSubmitted]       = useState(false);
  const [result, setResult]             = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);

  // ── Anti-cheat state ───────────────────────────────────────────────────────
  const [tabCount, setTabCount]         = useState(0);
  const [fsCount, setFsCount]           = useState(0);
  const [overlay, setOverlay]           = useState(null); // null | 'tab' | 'fs'
  const [autoSubmitting, setAutoSubmitting] = useState(false);

  // ── Behavioral state ───────────────────────────────────────────────────────
  const eventQueue      = useRef([]);
  const qEnterTime      = useRef(Date.now());
  const lastInteract    = useRef(Date.now());
  const firstActionMap  = useRef(new Set());
  const visitedMap      = useRef(new Set());
  const timeWarnFired   = useRef(false);

  const dispatchEvent = useCallback((type, payload = {}) => {
    eventQueue.current.push({ type, timestamp: new Date().toISOString(), ...payload });
  }, []);

  // ── Refs (always up-to-date inside event listeners / timers) ──────────────
  const tabRef          = useRef(0);
  const fsRef           = useRef(0);
  const submittedRef    = useRef(false);
  const answersRef      = useRef({});
  const questionsRef    = useRef([]);
  const currentRef      = useRef(0);
  const startTime       = useRef(Date.now());
  const fsEnteredOnce   = useRef(false);  // ignore FS exit before user ever enters FS
  const autoSubmitGuard = useRef(false);  // prevent double auto-submit
  const timerFired      = useRef(false);  // prevent double timer auto-submit
  const fsLaunched      = useRef(false);  // enter FS only once

  // Keep answer/question/current refs in sync with state
  useEffect(() => { answersRef.current  = answers;   }, [answers]);
  useEffect(() => { questionsRef.current = questions; }, [questions]);
  useEffect(() => { currentRef.current = current; }, [current]);

  // ─────────────────────────────────────────────────────────────────────────
  // FULLSCREEN HELPERS
  // ─────────────────────────────────────────────────────────────────────────
  const enterFullscreen = useCallback(() => {
    const el = document.documentElement;
    try {
      if      (el.requestFullscreen)           el.requestFullscreen();
      else if (el.webkitRequestFullscreen)     el.webkitRequestFullscreen();
      else if (el.mozRequestFullScreen)        el.mozRequestFullScreen();
    } catch (_) {}
  }, []);

  const exitFullscreen = useCallback(() => {
    // Guard: only call if document is active AND something is actually fullscreen
    if (!document.fullscreenElement &&
        !document.webkitFullscreenElement &&
        !document.mozFullScreenElement) return;
    try {
      if      (document.exitFullscreen)           document.exitFullscreen().catch(() => {});
      else if (document.webkitExitFullscreen)     document.webkitExitFullscreen();
      else if (document.mozCancelFullScreen)      document.mozCancelFullScreen();
    } catch (_) {}
  }, []);

  const isInFullscreen = () =>
    !!(document.fullscreenElement ||
       document.webkitFullscreenElement ||
       document.mozFullScreenElement);

  // ─────────────────────────────────────────────────────────────────────────
  // SUBMIT  — uses refs so it's never stale inside closures / timers
  // ─────────────────────────────────────────────────────────────────────────
  const doSubmit = useCallback(async ({ forceCheat = false, timeUp = false } = {}) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitted(true);

    if (!isMock) exitFullscreen();

    const timeTaken  = Math.floor((Date.now() - startTime.current) / 1000);
    const answersArr = questionsRef.current.map(q => ({
      question_id:     q.id,
      selected_option: answersRef.current[q.id] ?? null,
    }));

    try {
      const res = await API.post(`/tests/${id}/submit`, {
        answers:            answersArr,
        time_taken_seconds: timeTaken,
        tab_switch_count:   tabRef.current,
        fs_exit_count:      fsRef.current,
        force_cheat:        forceCheat,
        time_up:            timeUp,
      });
      setResult({ ...res.data.result, forceCheat, timeUp });
    } catch (err) {
      if (err.response?.data?.message === 'Test already submitted') {
        navigate('/student/tests');
      } else {
        setResult({
          error: true, forceCheat, timeUp,
          score: 0, correct: 0, wrong: 0,
          total: questionsRef.current.length, percentage: '0',
        });
      }
    }
  }, [id, isMock, navigate, exitFullscreen]);

  // ─────────────────────────────────────────────────────────────────────────
  // TRIGGER AUTO-SUBMIT  (guarded — fires exactly once)
  // ─────────────────────────────────────────────────────────────────────────
  const triggerAutoSubmit = useCallback((reason) => {
    if (autoSubmitGuard.current || submittedRef.current) return;
    autoSubmitGuard.current = true;
    setAutoSubmitting(true);
    setOverlay(reason);          // overlay stays open showing "auto-submitted"
    setTimeout(() => doSubmit({ forceCheat: true }), 3000);
  }, [doSubmit]);

  // ─────────────────────────────────────────────────────────────────────────
  // ANTI-CHEAT EVENT LISTENERS  (real tests only, registered once)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isMock) return; // ← ZERO anti-cheat for mock tests

    // ── 1. Tab / visibility detection ─────────────────────────────────────
    let hiddenTimer = null;
    const onVisibility = () => {
      if (document.hidden) {
        dispatchEvent('tab_blur');
        // Debounce 600 ms to filter accidental OS focus loss
        hiddenTimer = setTimeout(() => {
          if (submittedRef.current) return;
          const next = tabRef.current + 1;
          tabRef.current = next;
          setTabCount(next);
          if (next >= TAB_LIMIT) {
            triggerAutoSubmit('tab');
          } else if (next >= TAB_WARN_AT) {
            setOverlay('tab');
          }
        }, 600);
      } else {
        dispatchEvent('tab_focus');
        // Came back within debounce window — cancel
        clearTimeout(hiddenTimer);
        hiddenTimer = null;
      }
    };

    // ── 2. Fullscreen exit detection ──────────────────────────────────────
    const onFsChange = () => {
      if (submittedRef.current) return;
      if (isInFullscreen()) {
        // Student entered fullscreen — start tracking exits from now
        fsEnteredOnce.current = true;
        return;
      }
      // Don't penalise before they've ever entered fullscreen
      if (!fsEnteredOnce.current) return;

      const next = fsRef.current + 1;
      fsRef.current = next;
      setFsCount(next);
      if (next >= FS_LIMIT) {
        triggerAutoSubmit('fs');
      } else if (next >= FS_WARN_AT) {
        setOverlay('fs');
      }
    };

    // ── 3. Block right-click ──────────────────────────────────────────────
    const onContextMenu = (e) => e.preventDefault();

    // ── 4. Block copy / cut / paste ───────────────────────────────────────
    const onCopy  = (e) => e.preventDefault();
    const onCut   = (e) => e.preventDefault();
    const onPaste = (e) => e.preventDefault();

    // ── 5. Block dangerous keyboard shortcuts ────────────────────────────
    const onKeyDown = (e) => {
      if (submittedRef.current) return;
      const k = e.key.toLowerCase();
      if (e.key === 'F12')                                              { e.preventDefault(); return; }
      if (e.key === 'PrintScreen')                                      { e.preventDefault(); return; }
      if (e.ctrlKey && e.shiftKey && ['i','j','c','u'].includes(k))    { e.preventDefault(); return; }
      if (e.ctrlKey && k === 'u')                                       { e.preventDefault(); return; }
    };

    // ── 6. Warn before tab close / navigate away ─────────────────────────
    const onBeforeUnload = (e) => {
      if (submittedRef.current) return;
      e.preventDefault();
      e.returnValue = 'Your test is in progress. Leaving will count as a violation.';
    };

    // ── 7. Idle detection ────────────────────────────────────────────────
    const idleChecker = setInterval(() => {
      if (submittedRef.current) return;
      if (Date.now() - lastInteract.current > 30000) {
        const activeQ = questionsRef.current[currentRef.current];
        dispatchEvent('idle_detected', { 
          question_id: activeQ?.id,
          metadata: { idle_duration_ms: Date.now() - lastInteract.current } 
        });
        lastInteract.current = Date.now(); // reset to prevent spam
      }
    }, 5000);

    const onInteract = () => { lastInteract.current = Date.now(); };
    window.addEventListener('mousemove', onInteract);
    window.addEventListener('keydown', onInteract);
    window.addEventListener('click', onInteract);

    // ── 8. Batch Event Flusher ───────────────────────────────────────────
    const flushInterval = setInterval(async () => {
      if (eventQueue.current.length === 0 || submittedRef.current) return;
      const eventsToSend = [...eventQueue.current];
      eventQueue.current = [];
      try {
        await API.post(`/tests/${id}/events`, { events: eventsToSend });
      } catch (e) {
        eventQueue.current.unshift(...eventsToSend);
      }
    }, 10000);

    document.addEventListener('visibilitychange',       onVisibility);
    document.addEventListener('fullscreenchange',       onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    document.addEventListener('mozfullscreenchange',    onFsChange);
    document.addEventListener('contextmenu',            onContextMenu);
    document.addEventListener('copy',                   onCopy);
    document.addEventListener('cut',                    onCut);
    document.addEventListener('paste',                  onPaste);
    document.addEventListener('keydown',                onKeyDown);
    window.addEventListener('beforeunload',             onBeforeUnload);

    return () => {
      clearTimeout(hiddenTimer);
      clearInterval(idleChecker);
      clearInterval(flushInterval);
      window.removeEventListener('mousemove', onInteract);
      window.removeEventListener('keydown', onInteract);
      window.removeEventListener('click', onInteract);
      document.removeEventListener('visibilitychange',       onVisibility);
      document.removeEventListener('fullscreenchange',       onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
      document.removeEventListener('mozfullscreenchange',    onFsChange);
      document.removeEventListener('contextmenu',            onContextMenu);
      document.removeEventListener('copy',                   onCopy);
      document.removeEventListener('cut',                    onCut);
      document.removeEventListener('paste',                  onPaste);
      document.removeEventListener('keydown',                onKeyDown);
      window.removeEventListener('beforeunload',             onBeforeUnload);
    };
  }, [isMock, triggerAutoSubmit]); // stable — runs exactly once

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH TEST
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchTest = async () => {
      try {
        const res = await API.get(`/tests/${id}/student`);
        const { test: t, questions: qs, scheduledAt: perSAt } = res.data;

        setTest(t);
        setQuestions(qs);
        questionsRef.current = qs;
        setTimeLeft((t.duration_minutes || 30) * 60);

        const globalSAt = t.scheduled_at || null;
        setScheduledAt(perSAt || null);
        setGlobalScheduledAt(globalSAt);
        resolveStatus(globalSAt, perSAt || null);

      } catch (err) {
        const msg     = err.response?.data?.message;
        const attempt = err.response?.data?.attempt;

        if (msg === 'already_submitted' && attempt) {
          const total = attempt.total_questions || 0;
          setTest({ test_name: 'Test', total_marks: total });
          submittedRef.current = true;
          setSubmitted(true);
          setResult({
            score:             attempt.score || 0,
            correct:           attempt.correct_answers || 0,
            wrong:             attempt.wrong_answers   || 0,
            total,
            percentage:
              total > 0
                ? ((attempt.score / total) * 100).toFixed(1)
                : '0',
            cheating_flag:     attempt.cheating_flag || false,
            already_submitted: true,
          });
          setTestStatus('ready');
        } else {
          showAlert('Error', err.response?.data?.message || 'Failed to load test.');
          navigate('/student/tests');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchTest();
  }, [id, navigate]); // eslint-disable-line

  // ─────────────────────────────────────────────────────────────────────────
  // TIMING  (waiting / ready / closed)
  // ─────────────────────────────────────────────────────────────────────────
  const resolveStatus = useCallback((globalSAt, perSAt) => {
    const effective = perSAt || globalSAt;
    if (!effective) { setTestStatus('ready'); return; }
    const now    = Date.now();
    const start  = new Date(effective).getTime();
    const grace  = start + 5  * 60_000;
    const closes = start + 10 * 60_000;
    if      (now < grace)  setTestStatus('waiting');
    else if (now < closes) setTestStatus('ready');
    else                   setTestStatus('closed');
  }, []);

  // Poll while waiting for the scheduled time
  useEffect(() => {
    if (submitted || testStatus === 'ready' || testStatus === 'closed') return;
    if (!scheduledAt && !globalScheduledAt) return;
    const iv = setInterval(() => resolveStatus(globalScheduledAt, scheduledAt), 1000);
    return () => clearInterval(iv);
  }, [scheduledAt, globalScheduledAt, submitted, testStatus, resolveStatus]);

  // TestWaitingPage calls this when its countdown hits zero
  const handleTimeReached = useCallback(() => setTestStatus('ready'), []);

  // ─────────────────────────────────────────────────────────────────────────
  // ENTER FULLSCREEN once test becomes ready  (real tests only)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isMock)                  return;
    if (fsLaunched.current)      return;
    if (testStatus !== 'ready')  return;
    if (submitted)               return;
    if (questions.length === 0)  return;
    fsLaunched.current = true;
    setTimeout(enterFullscreen, 400); // short delay so UI has settled
  }, [testStatus, submitted, questions.length, isMock, enterFullscreen]);

  // ─────────────────────────────────────────────────────────────────────────
  // TIMER COUNTDOWN
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (testStatus !== 'ready')              return;
    if (submitted || questions.length === 0) return;

    const iv = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(iv);
          // Fire auto-submit outside the setState callback
          if (!timerFired.current && !submittedRef.current) {
            timerFired.current = true;
            setTimeout(() => doSubmit({ timeUp: true }), 0);
          }
          return 0;
        }
        if (prev <= 300 && prev > 1 && !timeWarnFired.current) {
          timeWarnFired.current = true;
          dispatchEvent('time_warning_reaction', { metadata: { time_left_seconds: prev } });
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(iv);
  }, [testStatus, submitted, questions.length, doSubmit]);

  // ─────────────────────────────────────────────────────────────────────────
  // QUESTION VISIT TRACKING
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!questions || questions.length === 0 || submitted || testStatus !== 'ready') return;
    const q = questions[current];
    if (!q) return;

    if (visitedMap.current.has(q.id)) {
      dispatchEvent('question_revisited', { question_id: q.id });
    } else {
      dispatchEvent('question_opened', { question_id: q.id });
      visitedMap.current.add(q.id);
    }
    qEnterTime.current = Date.now();

    return () => {
      // Cleanup: user is leaving the question
      const timeSpent = Date.now() - qEnterTime.current;
      dispatchEvent('question_left', { 
        question_id: q.id, 
        time_spent_ms: timeSpent 
      });

      if (!answersRef.current[q.id]) {
        dispatchEvent('question_skipped', { question_id: q.id });
      }
    };
  }, [current, questions, submitted, testStatus, dispatchEvent]);

  // ─────────────────────────────────────────────────────────────────────────
  // OVERLAY DISMISS HANDLERS
  // ─────────────────────────────────────────────────────────────────────────
  const dismissTabOverlay = useCallback(() => {
    if (autoSubmitting) return;
    setOverlay(null);
  }, [autoSubmitting]);

  const dismissFsOverlay = useCallback(() => {
    if (autoSubmitting) return;
    setOverlay(null);
    enterFullscreen();
  }, [autoSubmitting, enterFullscreen]);

  // ─────────────────────────────────────────────────────────────────────────
  // QUESTION NAVIGATOR STATUS  (memoized — stable, no re-renders from timer)
  // ─────────────────────────────────────────────────────────────────────────
  const getNavStatus = useCallback((idx) => {
    const q = questions[idx];
    if (!q) return 'unvisited';
    if (marked.has(idx))             return 'marked';
    if (answers[q.id] !== undefined) return 'answered';
    return 'unvisited';
  }, [questions, marked, answers]);

  const formatTime = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER GUARDS
  // ─────────────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="tt-loading">
      <div style={{ fontSize: 28, marginBottom: 8 }}>⏳</div>
      Loading test…
    </div>
  );

  if (testStatus === 'waiting' && test && !result) {
    return (
      <TestWaitingPage
        testName={test.test_name}
        testDuration={test.duration_minutes}
        testMarks={test.total_marks}
        scheduledTime={scheduledAt || globalScheduledAt}
        onTimeReached={handleTimeReached}
      />
    );
  }

  if (testStatus === 'closed' && !submitted && !result && test) {
    return (
      <div className="tt-closed-screen">
        <div className="tt-closed-card">
          <div className="tt-closed-icon">❌</div>
          <h1 className="tt-closed-title">Test Window Closed</h1>
          <p className="tt-closed-message">The allowed time window for this test has expired.</p>
          <p className="tt-closed-submsg">Contact your administrator if you believe this is an error.</p>
          <button className="tt-close-btn" onClick={() => navigate('/student/tests')}>
            ← Back to Tests
          </button>
        </div>
      </div>
    );
  }

  if (result) {
    const pct  = result.percentage ? parseFloat(result.percentage) : (result.total > 0 ? ((result.score / result.total) * 100).toFixed(1) : 0);
    const pass = pct >= 60;
    return (
      <div className="tt-result-screen">
        <div className="tt-result-card">
          <div className="tt-result-icon">
            {result.forceCheat ? '🚫' : result.timeUp ? '⏰' : pass ? '🎉' : '📚'}
          </div>

          <h2 className="tt-result-title">
            {result.forceCheat          ? 'Test Auto-Submitted'
             : result.already_submitted ? 'Already Submitted'
             : result.timeUp            ? 'Time Up!'
             : pass                     ? 'Congratulations!'
                                        : 'Keep Practicing!'}
          </h2>

          <p className="tt-result-sub">
            {result.forceCheat
              ? 'Your test was auto-submitted due to repeated policy violations.'
              : result.already_submitted
                ? 'You have already completed this test.'
                : result.timeUp
                  ? 'Your time ran out — all answers have been saved automatically.'
                  : `${test?.test_name || 'Test'} — Completed`}
          </p>

          <div className="tt-result-score">
            <div className="tt-big-score" style={{ color: pass ? '#16a085' : '#e94560' }}>
              {result.score} / {result.total}
            </div>
            <div className="tt-big-pct" style={{ color: pass ? '#16a085' : '#e94560' }}>
              {pct}%
            </div>
          </div>

          <div style={{
            background: pass ? '#e6f9f0' : '#fff0f0',
            color:      pass ? '#1a7f3c' : '#cc0000',
            padding: '10px 24px', borderRadius: 10,
            fontWeight: 700, fontSize: 16, marginBottom: 20,
          }}>
            {pass ? '✓ PASSED' : '✗ FAILED'} — {pct}%
          </div>

          <div className="tt-result-stats">
            {[
              { label: 'Correct', val: result.correct, color: '#16a085' },
              { label: 'Wrong',   val: result.wrong,   color: '#e94560' },
              {
                label: 'Skipped',
                val:   Math.max(0, result.total - result.correct - result.wrong),
                color: '#888',
              },
            ].map(s => (
              <div className="tt-rs" key={s.label}>
                <div className="tt-rs-val"   style={{ color: s.color }}>{s.val}</div>
                <div className="tt-rs-label">{s.label}</div>
              </div>
            ))}
          </div>

          {result.forceCheat && (
            <div className="tt-cheat-warning" style={{ background: '#fff0f0', color: '#cc0000' }}>
              🚫 Test was auto-submitted due to repeated policy violations.
            </div>
          )}
          {!result.forceCheat && result.cheating_flag && (
            <div className="tt-cheat-warning">
              ⚠️ Policy violations were detected during this test.
            </div>
          )}
          {result.error && (
            <div className="tt-cheat-warning" style={{ background: '#fff0f0', color: '#cc0000' }}>
              ⚠️ There was an issue saving your result. Please contact your administrator.
            </div>
          )}

          <div className="tt-result-actions">
            <button className="tt-btn-home"  onClick={() => navigate('/student/tests')}>
              View My Tests
            </button>
            <button className="tt-btn-tests" onClick={() => navigate('/student/dashboard')}>
              Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!test || questions.length === 0) return (
    <div className="tt-loading">
      No questions found for this test. Please contact admin.
      <br /><br />
      <button
        onClick={() => navigate('/student/tests')}
        style={{ padding: '8px 20px', background: '#0f3460', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}
      >
        Go Back
      </button>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // TEST UI
  // ─────────────────────────────────────────────────────────────────────────
  const q        = questions[current];
  const answered = Object.keys(answers).length;

  return (
    <div
      className="tt-wrapper"
      /* Disable text selection for real proctored tests */
      style={!isMock ? { userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none' } : {}}
    >

      {/* ── Anti-cheat overlay (real tests only) ──────────────────────────── */}
      {!isMock && overlay === 'tab' && (
        <Overlay
          kind="tab"
          count={tabCount}
          limit={TAB_LIMIT}
          testName={test.test_name}
          onDismiss={dismissTabOverlay}
          autoSubmitting={autoSubmitting}
        />
      )}
      {!isMock && overlay === 'fs' && (
        <Overlay
          kind="fs"
          count={fsCount}
          limit={FS_LIMIT}
          testName={test.test_name}
          onDismiss={dismissFsOverlay}
          autoSubmitting={autoSubmitting}
        />
      )}

      {/* ── Top bar ──────────────────────────────────────────────────────────── */}
      <div className="tt-topbar">
        <div className="tt-topbar-left">
          <div className="tt-test-name">{test.test_name}</div>
          <div className="tt-session">
            Session: #AP-{id}-{String(current).padStart(4, '0')}
            {!isMock && (
              <span className="tt-proctored-badge">🔒 Proctored</span>
            )}
          </div>
        </div>

        <div className="tt-topbar-center">
          <div className={`tt-timer ${timeLeft < 300 ? 'danger' : ''}`}>
            ⏱ {formatTime(timeLeft)}
          </div>
        </div>

        <div className="tt-topbar-right">
          {/* Live violation badges — appear after first violation */}
          {!isMock && tabCount > 0 && (
            <span className={`tt-violation-badge ${tabCount >= TAB_LIMIT - 1 ? 'danger' : 'warn'}`}>
              ⚠ Tabs {tabCount}/{TAB_LIMIT - 1}
            </span>
          )}
          {!isMock && fsCount > 0 && (
            <span className={`tt-violation-badge ${fsCount >= FS_LIMIT - 1 ? 'danger' : 'warn'}`}>
              ⚠ FS {fsCount}/{FS_LIMIT - 1}
            </span>
          )}
        </div>
      </div>

      {/* ── Progress bar ──────────────────────────────────────────────────────── */}
      <div className="tt-progress-bar">
        <div className="tt-progress-fill"
          style={{ width: `${(answered / questions.length) * 100}%` }} />
      </div>

      <div className="tt-body">

        {/* ── Question area ─────────────────────────────────────────────────── */}
        <div className="tt-question-area">
          <div className="tt-q-header">
            <span className="tt-q-num">QUESTION {current + 1} OF {questions.length}</span>
            <span className="tt-q-diff">
              Difficulty: {q.difficulty?.charAt(0).toUpperCase() + q.difficulty?.slice(1)}
            </span>
          </div>

          <div className="tt-q-text">{q.question_text}</div>

          <div className="tt-options">
            {(q.options || []).map((opt, i) => {
              const optNum   = i + 1;
              const selected = answers[q.id] === optNum;
              return (
                <div
                  key={i}
                  className={`tt-option${selected ? ' selected' : ''}`}
                  onClick={() => {
                    if (!submitted) {
                      const prevAns = answers[q.id];
                      if (!prevAns) {
                        if (!firstActionMap.current.has(q.id)) {
                          firstActionMap.current.add(q.id);
                          dispatchEvent('first_interaction', { 
                            question_id: q.id, 
                            metadata: { time_to_first_action_ms: Date.now() - qEnterTime.current, action_type: 'option_select' } 
                          });
                        }
                        dispatchEvent('option_selected', { 
                          question_id: q.id, 
                          from_option: null, 
                          to_option: optNum,
                          time_to_first_answer_ms: Date.now() - qEnterTime.current
                        });
                      } else if (prevAns !== optNum) {
                        dispatchEvent('option_changed', { question_id: q.id, from_option: prevAns, to_option: optNum });
                      }
                      setAnswers(p => ({ ...p, [q.id]: optNum }));
                      setShowExplanation(false);
                    }
                  }}
                >
                  <span className="tt-opt-letter">{String.fromCharCode(65 + i)}</span>
                  <span className="tt-opt-text">{opt}</span>
                </div>
              );
            })}
          </div>

          {q.explanation && (
            <div className="tt-explanation-toggle"
              onClick={() => setShowExplanation(p => !p)}>
              <span>ℹ View Step-by-Step Explanation</span>
              <span>{showExplanation ? '▲' : '▼'}</span>
            </div>
          )}
          {showExplanation && q.explanation && (
            <div className="tt-explanation">{q.explanation}</div>
          )}

          <div className="tt-bottom-nav">
            <button
              className="tt-btn-prev"
              disabled={current === 0}
              onClick={() => { setCurrent(p => p - 1); setShowExplanation(false); }}
            >
              ← Previous
            </button>
            <button
              className="tt-btn-mark"
              onClick={() => setMarked(p => {
                const n = new Set(p);
                n.has(current) ? n.delete(current) : n.add(current);
                return n;
              })}
            >
              {marked.has(current) ? '★ Marked' : '☆ Mark for Review'}
            </button>
            {current < questions.length - 1 ? (
              <button className="tt-btn-next"
                onClick={() => { setCurrent(p => p + 1); setShowExplanation(false); }}>
                Save & Next →
              </button>
            ) : (
              <button className="tt-btn-submit"
                onClick={async () => {
                  const confirmed = await showConfirm(
                    'Finish Test',
                    `Submit test?\nAnswered: ${answered} / ${questions.length}\n${questions.length - answered} question(s) unanswered.`
                  );
                  if (confirmed) doSubmit();
                }}>
                Finish & Submit
              </button>
            )}
          </div>
        </div>

        {/* ── Right sidebar ──────────────────────────────────────────────────── */}
        <div className="tt-sidebar">
          <div className="tt-nav-header">
            Question Navigator
            <span className="tt-nav-answered">{answered}/{questions.length}</span>
          </div>

          <QuestionNavigator
            questions={questions}
            current={current}
            getNavStatus={getNavStatus}
            setCurrent={setCurrent}
            setShowExplanation={setShowExplanation}
          />

          <div className="tt-nav-legend">
            <div className="tt-leg"><span className="tt-leg-dot answered" />Answered</div>
            <div className="tt-leg"><span className="tt-leg-dot marked"   />Marked</div>
            <div className="tt-leg"><span className="tt-leg-dot unvisited"/>Unvisited</div>
          </div>

          <button className="tt-finish-btn"
            onClick={async () => {
              const confirmed = await showConfirm('Finish Session', `Submit now? Answered ${answered}/${questions.length}.`);
              if (confirmed) doSubmit();
            }}>
            Finish Session
          </button>

          {isMock ? (
            <button className="tt-pause-btn" onClick={() => navigate('/student/mock')}>
              Exit Mock Test
            </button>
          ) : (
            <div className="tt-proctor-box">
              <div className="tt-proctor-title">🔒 Proctored Test</div>
              <div className="tt-proctor-rule">· Stay on this tab at all times</div>
              <div className="tt-proctor-rule">· Keep fullscreen mode active</div>
              <div className="tt-proctor-rule">· No copying or right-clicking</div>
              <div className="tt-proctor-rule">· {TAB_LIMIT} tab violations = auto-submit</div>
              <div className="tt-proctor-rule">· {FS_LIMIT} fullscreen exits = auto-submit</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TakeTest;
